import { apiClient } from '@/lib/api-client'
import { academicsPaths, schoolsPaths } from '@/lib/api-paths'
import { cleanApiParams } from '@/lib/clean-api-params'
import { coalesceEntriesOrRows } from '@/lib/coalesce-entries-or-rows'
import type {
  AcademicActionWindow,
  AcademicActionWindowMutationResult,
  AcademicClass,
  AddRosterEntryPayload,
  BulkAddRosterPayload,
  BulkAddRosterResult,
  AssignmentCategory,
  AttendanceRecord,
  AttendanceSummary,
  AttendanceSummaryEntry,
  AttendanceSummaryParams,
  BulkAttendancePayload,
  ReportCardBulkGeneratePayload,
  ReportCardBulkJob,
  BulkSaveGradesPayload,
  ClassScheduleSlot,
  ClassSession,
  ScheduledOffering,
  PlacementConflictCheckItem,
  PlacementConflictCheckResult,
  ScheduleConflictGroup,
  CreateSchoolYearPayload,
  UpdateSchoolYearPayload,
  AcademicClassBulkRequest,
  AcademicClassBulkResponse,
  BulkAssignTeacherPayload,
  BulkAssignTeacherResult,
  AcademicClassBulkItemWire,
  BulkAcademicClassItemInput,
  CreateAcademicClassPayload,
  CreateGradeScalePayload,
  CreateAssignmentCategoryPayload,
  CreateGradebookAssignmentPayload,
  CreateAcademicActionWindowPayload,
  CreateMarkingPeriodPayload,
  CreateRolloverPlanPayload,
  CreateScheduledOfferingPayload,
  ScheduledClassOfferingBulkRequest,
  ScheduledClassOfferingBulkResponse,
  ExecuteRolloverPayload,
  GradebookAssignment,
  GradeScale,
  GradeScaleRange,
  GradeChangeLog,
  GradeChangeLogEntry,
  GradebookSummary,
  ListAttendanceParams,
  ListClassesParams,
  ListSchoolYearsParams,
  ListReportCardsParams,
  ListTranscriptsParams,
  MarkingPeriod,
  MarkingPeriodFinalizeResult,
  MarkingPeriodMutationResult,
  MarkingPeriodGradeStatus,
  ReplaceGradeScaleRangesPayload,
  ReportCardCorrectPayload,
  ReportCardDetail,
  ReportCardGeneratePayload,
  ReportCardListItem,
  Rollover,
  RosterEntry,
  SchoolYear,
  SchoolYearCreateResult,
  SchoolYearFeeDefaults,
  SchoolYearFeesSnapshot,
  SchoolYearGradeFee,
  SchoolYearOverview,
  SchoolYearUpdateResult,
  UpdateSchoolYearFeeDefaultsPayload,
  UpdateSchoolYearGradeFeePayload,
  StudentGradeEntry,
  SubjectComment,
  TimetableListParams,
  OfferingInstructorsReplacePayload,
  OfferingStudentOverridesReplacePayload,
  OfferingStudentOverrideRow,
  PeriodOfferingRow,
  PeriodOfferingsParams,
  RotationWeekdayAssignmentInput,
  ScheduleConflictsParams,
  TranscriptDetail,
  TranscriptGeneratePayload,
  TranscriptListItem,
  UpdateAcademicClassPayload,
  UpdateGradeScalePayload,
  UpdateAssignmentCategoryPayload,
  UpdateGradebookAssignmentPayload,
  UpdateAcademicActionWindowPayload,
  UpdateMarkingPeriodPayload,
  UpdateRosterEntryPayload,
  UpdateScheduledOfferingPayload,
  UpsertSubjectCommentPayload,
  SchoolSchedule,
  SchoolScheduleConfig,
  PatchSchoolScheduleConfigPayload,
  SchedulePreviewResult,
  SchoolSchedulePreviewParams,
  ResolvedSessionsResult,
  ResolvedSessionsParams,
  DayType,
  DayTypePeriod,
  CreateDayTypePayload,
  UpdateDayTypePayload,
  CreateDayTypePeriodPayload,
  ReplaceDayTypePeriodsPayload,
  ReplaceDayTypePeriodsResult,
  UpdateDayTypePeriodPayload,
  PutRotationWeekdayAssignmentsPayload,
  TeacherAttentionListItem,
  TeacherTimetableSession,
  TeacherUpcomingAssignment,
  TeacherWeeklyClassAttendance,
  TeacherMeQueryParams,
  TeacherWeeklyAttendancePayload,
} from '../types/api'
import { toMarkingPeriodFinalizeResult } from '../types/api'
import type {
  ReportCardBulkPublishResult,
  ReportCardStatusSummary,
} from '../types/reportCardsTranscripts'
import { extractRotationWeekdayRowsFromSchedulePayload } from '../utils/extractRotationWeekdayRows'
import { unwrapAcademicsMutation } from '../utils/academicsMutationEnvelope'
import { groupAttendanceRows, type AttendanceRowDto } from '../utils/attendanceRecords'
import { localIsoDate, localIsoDateDaysAgo } from '../utils/localDate'

const normalizeSchoolYear = (
  schoolYear: Partial<SchoolYear>,
  fallbackSchoolId?: string,
): SchoolYear => ({
  id: schoolYear.id ?? '',
  school_id: schoolYear.school_id ?? fallbackSchoolId ?? '',
  school_name: schoolYear.school_name ?? '',
  name: schoolYear.name ?? schoolYear.id ?? 'Unnamed School Year',
  start_date: schoolYear.start_date ?? '',
  end_date: schoolYear.end_date ?? '',
  status: schoolYear.status ?? 'PLANNING',
  is_current: schoolYear.is_current ?? false,
  is_registration_open: schoolYear.is_registration_open ?? true,
  created_by_id: schoolYear.created_by_id ?? null,
  created_at: schoolYear.created_at ?? '',
  updated_at: schoolYear.updated_at ?? '',
})

export const schoolYearsApi = {
  async list(params: ListSchoolYearsParams = {}): Promise<SchoolYear[]> {
    const res = await apiClient.get(academicsPaths.schoolYears, {
      params: cleanApiParams(params as unknown as Record<string, unknown>),
    })
    const json = res.data as { success?: boolean; data?: SchoolYear[] }
    const items = json.data ?? (Array.isArray(res.data) ? res.data : [])
    return items.map(item => normalizeSchoolYear(item, params.school_id))
  },

  async createWithResult(payload: CreateSchoolYearPayload): Promise<SchoolYearCreateResult> {
    const res = await apiClient.post(academicsPaths.schoolYears, payload)
    const { data, date_policy_warnings, message } =
      unwrapAcademicsMutation<Partial<SchoolYear>>(res)
    return {
      data: normalizeSchoolYear(data, payload.school_id),
      date_policy_warnings,
      message,
    }
  },

  async create(payload: CreateSchoolYearPayload): Promise<SchoolYear> {
    const r = await this.createWithResult(payload)
    return r.data
  },

  async get(schoolYearId: string): Promise<SchoolYear> {
    const res = await apiClient.get(academicsPaths.schoolYear(schoolYearId))
    return res.data.data ?? res.data
  },

  async setCurrent(schoolYearId: string): Promise<SchoolYear> {
    const res = await apiClient.post(academicsPaths.schoolYearSetCurrent(schoolYearId))
    return res.data.data ?? res.data
  },

  async updateWithResult(
    schoolYearId: string,
    payload: UpdateSchoolYearPayload,
  ): Promise<SchoolYearUpdateResult> {
    const res = await apiClient.patch(academicsPaths.schoolYear(schoolYearId), payload)
    const { data, date_policy_warnings, message } =
      unwrapAcademicsMutation<Partial<SchoolYear>>(res)
    const raw = data as Partial<SchoolYear>
    return {
      data: normalizeSchoolYear(raw, raw.school_id),
      date_policy_warnings,
      message,
    }
  },

  async update(schoolYearId: string, payload: UpdateSchoolYearPayload): Promise<SchoolYear> {
    const r = await this.updateWithResult(schoolYearId, payload)
    return r.data
  },
}

function toAcademicClassBulkItemWire(
  item: BulkAcademicClassItemInput,
  fallbackIndex: number,
): AcademicClassBulkItemWire {
  return {
    name: item.name,
    grade_level_id: Number(item.grade_level_id),
    grade_section_id: item.grade_section_id == null ? null : Number(item.grade_section_id),
    code: item.code ?? '',
    primary_teacher_id: item.primary_teacher_id == null ? null : Number(item.primary_teacher_id),
    is_active: item.is_active ?? true,
    client_index: item.client_index ?? fallbackIndex,
  }
}

function parseStudentCount(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number.parseInt(raw, 10)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

/** Detail/list payloads may omit `student_count` or use `studentCount` — keep UI stable. */
function normalizeAcademicClass(raw: unknown): AcademicClass {
  const r = raw as Record<string, unknown>
  const sc = r.student_count ?? r.studentCount
  return {
    ...(raw as AcademicClass),
    student_count: parseStudentCount(sc),
    scheduled_offering_count: parseStudentCount(r.scheduled_offering_count),
  }
}

export const classesApi = {
  async list(schoolYearId: string, params?: ListClassesParams): Promise<AcademicClass[]> {
    const res = await apiClient.get(academicsPaths.classes(schoolYearId), {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    const items = res.data.data ?? res.data
    return Array.isArray(items) ? items.map(normalizeAcademicClass) : []
  },

  async get(classId: string): Promise<AcademicClass> {
    const res = await apiClient.get(academicsPaths.classDetail(classId))
    return normalizeAcademicClass(res.data.data ?? res.data)
  },

  async create(schoolYearId: string, payload: CreateAcademicClassPayload): Promise<AcademicClass> {
    const res = await apiClient.post(academicsPaths.classes(schoolYearId), payload)
    return normalizeAcademicClass(res.data.data ?? res.data)
  },

  /** MAK-227 — optional `dry_run` (no DB writes; `created[].id` null in dry run). */
  async bulkCreate(
    schoolYearId: string,
    payload: AcademicClassBulkRequest,
    options?: { dryRun?: boolean },
  ): Promise<AcademicClassBulkResponse> {
    const body = {
      items: payload.items.map((item, i) => toAcademicClassBulkItemWire(item, i)),
    }
    const res = await apiClient.post(
      academicsPaths.classesBulk(schoolYearId),
      body,
      options?.dryRun ? { params: { dry_run: true } } : undefined,
    )
    const raw = res.data.data ?? res.data
    return raw as AcademicClassBulkResponse
  },

  async bulkAssignTeacher(
    schoolYearId: string,
    payload: BulkAssignTeacherPayload,
  ): Promise<BulkAssignTeacherResult> {
    const res = await apiClient.post(academicsPaths.classesBulkAssignTeacher(schoolYearId), payload)
    const raw = res.data.data ?? res.data
    return {
      teacher_id: raw?.teacher_id ?? null,
      updated_ids: Array.isArray(raw?.updated_ids) ? raw.updated_ids : [],
      skipped: Array.isArray(raw?.skipped) ? raw.skipped : [],
    }
  },

  async update(classId: string, payload: UpdateAcademicClassPayload): Promise<AcademicClass> {
    const res = await apiClient.patch(academicsPaths.classDetail(classId), payload)
    return normalizeAcademicClass(res.data.data ?? res.data)
  },

  async remove(classId: string): Promise<void> {
    await apiClient.delete(academicsPaths.classDetail(classId))
  },
}

export const rosterApi = {
  async list(classId: string): Promise<RosterEntry[]> {
    const res = await apiClient.get(academicsPaths.roster(classId))
    return res.data.data ?? res.data
  },

  async add(classId: string, payload: AddRosterEntryPayload): Promise<RosterEntry> {
    const res = await apiClient.post(academicsPaths.roster(classId), payload)
    return res.data.data ?? res.data
  },

  async bulkAdd(classId: string, payload: BulkAddRosterPayload): Promise<BulkAddRosterResult> {
    const res = await apiClient.post(academicsPaths.rosterBulk(classId), payload)
    const raw = res.data.data ?? res.data
    return {
      added: Array.isArray(raw?.added) ? raw.added : [],
      skipped: Array.isArray(raw?.skipped) ? raw.skipped : [],
    }
  },

  async updateStatus(
    classId: string,
    studentId: string,
    payload: UpdateRosterEntryPayload,
  ): Promise<RosterEntry> {
    const res = await apiClient.patch(academicsPaths.rosterEntry(classId, studentId), payload)
    return res.data.data ?? res.data
  },
}

export const attendanceApi = {
  /** The API returns the saved rows (one per student); callers get the session. */
  async bulkSubmit(classId: string, payload: BulkAttendancePayload): Promise<AttendanceRecord> {
    const res = await apiClient.post(academicsPaths.attendanceBulk(classId), payload)
    const raw = res.data.data ?? res.data
    const rows = Array.isArray(raw) ? (raw as AttendanceRowDto[]) : []
    const [session] = groupAttendanceRows(rows)
    if (!session) throw new Error('Attendance was saved but the server returned no rows.')
    return session
  },

  async list(classId: string, params?: ListAttendanceParams): Promise<AttendanceRecord[]> {
    const res = await apiClient.get(academicsPaths.attendance(classId), {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    const raw = res.data.data ?? res.data
    // A malformed body must fail loudly: an empty list would look like "no records"
    // and let the grid default every student to Present.
    if (!Array.isArray(raw)) throw new Error('Unexpected attendance response')
    return groupAttendanceRows(raw as AttendanceRowDto[])
  },

  async getSummary(classId: string, params: AttendanceSummaryParams): Promise<AttendanceSummary> {
    const res = await apiClient.get(academicsPaths.attendanceSummary(classId), {
      params: cleanApiParams(params as unknown as Record<string, unknown>),
    })
    const raw = (res.data.data ?? res.data) as Record<string, unknown>
    const list = coalesceEntriesOrRows<AttendanceSummaryEntry>(raw) ?? []
    return {
      ...(raw as unknown as AttendanceSummary),
      entries: list,
    }
  },
}

export const rolloverApi = {
  async plan(sourceSchoolYearId: string, payload: CreateRolloverPlanPayload): Promise<Rollover> {
    const res = await apiClient.post(academicsPaths.rolloverPlan(sourceSchoolYearId), payload)
    return res.data.data ?? res.data
  },

  async get(rolloverId: string): Promise<Rollover> {
    const res = await apiClient.get(academicsPaths.rollover(rolloverId))
    return res.data.data ?? res.data
  },

  async execute(rolloverId: string, payload: ExecuteRolloverPayload): Promise<Rollover> {
    const res = await apiClient.post(academicsPaths.rolloverExecute(rolloverId), payload)
    return res.data.data ?? res.data
  },

  /** Manual fallback: create re-enrollment registrations for a target year now. */
  async generateReenrollmentRegistrations(
    targetSchoolYearId: string,
  ): Promise<{ registrations_created: number }> {
    const res = await apiClient.post(academicsPaths.reenrollmentRegistrations(targetSchoolYearId))
    return res.data.data ?? res.data
  },
}

export const markingPeriodsApi = {
  async list(schoolYearId: string): Promise<MarkingPeriod[]> {
    const res = await apiClient.get(academicsPaths.markingPeriods(schoolYearId))
    return res.data.data ?? res.data
  },

  async create(
    schoolYearId: string,
    payload: CreateMarkingPeriodPayload,
  ): Promise<MarkingPeriodMutationResult> {
    const res = await apiClient.post(academicsPaths.markingPeriods(schoolYearId), payload)
    const { data, date_policy_warnings, message } = unwrapAcademicsMutation<MarkingPeriod>(res)
    return { data, date_policy_warnings, message }
  },

  async get(markingPeriodId: string): Promise<MarkingPeriod> {
    const res = await apiClient.get(academicsPaths.markingPeriod(markingPeriodId))
    return res.data.data ?? res.data
  },

  async update(
    markingPeriodId: string,
    payload: UpdateMarkingPeriodPayload,
  ): Promise<MarkingPeriodMutationResult> {
    const res = await apiClient.patch(academicsPaths.markingPeriod(markingPeriodId), payload)
    const { data, date_policy_warnings, message } = unwrapAcademicsMutation<MarkingPeriod>(res)
    return { data, date_policy_warnings, message }
  },

  async remove(markingPeriodId: string): Promise<void> {
    await apiClient.delete(academicsPaths.markingPeriod(markingPeriodId))
  },

  async finalize(markingPeriodId: string): Promise<MarkingPeriodFinalizeResult> {
    const res = await apiClient.post(academicsPaths.markingPeriodFinalize(markingPeriodId))
    const mp = (res.data.data ?? res.data) as MarkingPeriod
    return toMarkingPeriodFinalizeResult(mp)
  },

  async unfinalize(markingPeriodId: string): Promise<MarkingPeriodFinalizeResult> {
    const res = await apiClient.post(academicsPaths.markingPeriodUnfinalize(markingPeriodId))
    const mp = (res.data.data ?? res.data) as MarkingPeriod
    return toMarkingPeriodFinalizeResult(mp)
  },

  async getGradeStatus(markingPeriodId: string): Promise<MarkingPeriodGradeStatus> {
    const res = await apiClient.get(academicsPaths.markingPeriodGradeStatus(markingPeriodId))
    return res.data.data ?? res.data
  },
}

function normalizeAcademicActionWindow(raw: Partial<AcademicActionWindow>): AcademicActionWindow {
  return {
    id: raw.id ?? '',
    school_year_id: raw.school_year_id ?? '',
    action_type: raw.action_type ?? 'GRADE_ENTRY',
    opens_on: raw.opens_on ?? '',
    closes_on: raw.closes_on ?? '',
    marking_period_id: raw.marking_period_id ?? null,
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? '',
  }
}

/** MAK-228 — gradebook / attendance / report-card date-policy windows */
export const actionWindowsApi = {
  async list(schoolYearId: string): Promise<AcademicActionWindow[]> {
    const res = await apiClient.get(academicsPaths.schoolYearActionWindows(schoolYearId))
    const items = res.data.data ?? res.data
    return Array.isArray(items) ? items.map(i => normalizeAcademicActionWindow(i)) : []
  },

  async create(
    schoolYearId: string,
    payload: CreateAcademicActionWindowPayload,
  ): Promise<AcademicActionWindowMutationResult> {
    const res = await apiClient.post(academicsPaths.schoolYearActionWindows(schoolYearId), payload)
    const { data, date_policy_warnings, message } =
      unwrapAcademicsMutation<Partial<AcademicActionWindow>>(res)
    return {
      data: normalizeAcademicActionWindow(data),
      date_policy_warnings,
      message,
    }
  },

  async get(actionWindowId: string): Promise<AcademicActionWindow> {
    const res = await apiClient.get(academicsPaths.actionWindow(actionWindowId))
    const { data } = unwrapAcademicsMutation<Partial<AcademicActionWindow>>(res)
    return normalizeAcademicActionWindow(data)
  },

  async update(
    actionWindowId: string,
    payload: UpdateAcademicActionWindowPayload,
  ): Promise<AcademicActionWindowMutationResult> {
    const res = await apiClient.patch(academicsPaths.actionWindow(actionWindowId), payload)
    const { data, date_policy_warnings, message } =
      unwrapAcademicsMutation<Partial<AcademicActionWindow>>(res)
    return {
      data: normalizeAcademicActionWindow(data),
      date_policy_warnings,
      message,
    }
  },

  async remove(actionWindowId: string): Promise<void> {
    await apiClient.delete(academicsPaths.actionWindow(actionWindowId))
  },
}

function feeNum(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function normalizeSchoolYearFeeDefaults(
  raw: Partial<SchoolYearFeeDefaults> | Record<string, unknown> | null | undefined,
  schoolYearId: string,
): SchoolYearFeeDefaults {
  const r = (raw ?? {}) as Record<string, unknown>
  return {
    id: String(r.id ?? ''),
    school_year_id: String(r.school_year_id ?? schoolYearId),
    default_tuition_cadence: String(r.default_tuition_cadence ?? 'ANNUAL'),
    default_monthly_tuition_start_month:
      r.default_monthly_tuition_start_month == null
        ? null
        : Number(r.default_monthly_tuition_start_month),
    default_monthly_tuition_end_month:
      r.default_monthly_tuition_end_month == null
        ? null
        : Number(r.default_monthly_tuition_end_month),
    default_tuition_fee: feeNum(r.default_tuition_fee, 0),
    default_registration_fee: feeNum(r.default_registration_fee, 0),
    default_has_registration_fee: Boolean(r.default_has_registration_fee),
    default_activity_fee: r.default_activity_fee == null ? null : feeNum(r.default_activity_fee, 0),
    default_technology_fee:
      r.default_technology_fee == null ? null : feeNum(r.default_technology_fee, 0),
    default_book_fee: r.default_book_fee == null ? null : feeNum(r.default_book_fee, 0),
    default_has_book_fee: Boolean(r.default_has_book_fee),
    default_lunch_fee: r.default_lunch_fee == null ? null : feeNum(r.default_lunch_fee, 0),
    default_has_lunch_fee: Boolean(r.default_has_lunch_fee),
    default_trip_fee: r.default_trip_fee == null ? null : feeNum(r.default_trip_fee, 0),
    default_has_trip_fee: Boolean(r.default_has_trip_fee),
    default_custom_fee_name: String(r.default_custom_fee_name ?? ''),
    default_custom_fee_amount:
      r.default_custom_fee_amount == null ? null : feeNum(r.default_custom_fee_amount, 0),
    default_has_custom_fee: Boolean(r.default_has_custom_fee),
    default_payment_plan_enabled: Boolean(r.default_payment_plan_enabled),
    default_installments: Math.max(1, Math.floor(feeNum(r.default_installments, 1))),
    allowed_installment_counts: Array.isArray(r.allowed_installment_counts)
      ? (r.allowed_installment_counts as unknown[])
          .map(x => Number(x))
          .filter(n => Number.isFinite(n))
      : null,
    default_minimum_payment_amount:
      r.default_minimum_payment_amount == null ? null : feeNum(r.default_minimum_payment_amount, 0),
    created_at: String(r.created_at ?? ''),
    updated_at: String(r.updated_at ?? ''),
  }
}

function normalizeSchoolYearGradeFee(
  raw: Partial<SchoolYearGradeFee> | Record<string, unknown>,
): SchoolYearGradeFee {
  const r = raw as Record<string, unknown>
  return {
    id: String(r.id ?? ''),
    school_year_id: String(r.school_year_id ?? ''),
    grade_level_id: feeNum(r.grade_level_id, 0),
    grade_level_name: r.grade_level_name == null ? undefined : String(r.grade_level_name),
    use_form_defaults: Boolean(r.use_form_defaults ?? r.use_year_defaults),
    registration_fee: feeNum(r.registration_fee, 0),
    tuition_fee: feeNum(r.tuition_fee, 0),
    activity_fee: r.activity_fee == null ? null : feeNum(r.activity_fee, 0),
    technology_fee: r.technology_fee == null ? null : feeNum(r.technology_fee, 0),
    book_fee: r.book_fee == null ? null : feeNum(r.book_fee, 0),
    lunch_fee: r.lunch_fee == null ? null : feeNum(r.lunch_fee, 0),
    trip_fee: r.trip_fee == null ? null : feeNum(r.trip_fee, 0),
  }
}

function normalizeSchoolYearFeesSnapshot(
  raw: unknown,
  fallbackSchoolYearId: string,
): SchoolYearFeesSnapshot {
  const root = raw as Record<string, unknown>
  const payload = (root.data ?? root) as Record<string, unknown>
  const schoolYearId = String(payload.school_year_id ?? fallbackSchoolYearId)
  const defaultsRaw = payload.defaults ?? payload.default
  const gradeList = payload.grades ?? payload.grade_fees ?? payload.gradeFees
  const defaults =
    defaultsRaw && typeof defaultsRaw === 'object'
      ? normalizeSchoolYearFeeDefaults(defaultsRaw as Record<string, unknown>, schoolYearId)
      : null
  const grade_fees = Array.isArray(gradeList)
    ? gradeList.map(g => normalizeSchoolYearGradeFee(g as Record<string, unknown>))
    : []
  const is_configured = Boolean(
    payload.is_configured ?? payload.is_configured_defaults ?? defaults !== null,
  )
  return {
    school_year_id: schoolYearId,
    is_configured,
    defaults,
    grade_fees,
  }
}

/** MAK-57 — school-year canonical registration/tuition configuration */
export const schoolYearFeesApi = {
  async getSnapshot(schoolYearId: string): Promise<SchoolYearFeesSnapshot> {
    const res = await apiClient.get(academicsPaths.schoolYearFees(schoolYearId))
    const raw = res.data.data ?? res.data
    return normalizeSchoolYearFeesSnapshot(raw, schoolYearId)
  },

  async putDefaults(
    schoolYearId: string,
    payload: UpdateSchoolYearFeeDefaultsPayload,
  ): Promise<SchoolYearFeeDefaults> {
    const res = await apiClient.put(academicsPaths.schoolYearFeeDefaults(schoolYearId), payload)
    const raw = res.data.data ?? res.data
    return normalizeSchoolYearFeeDefaults(raw as Record<string, unknown>, schoolYearId)
  },

  async listGradeFees(schoolYearId: string): Promise<SchoolYearGradeFee[]> {
    const snapshot = await this.getSnapshot(schoolYearId)
    return snapshot.grade_fees
  },

  async putGradeFee(
    schoolYearId: string,
    gradeLevelId: number,
    payload: UpdateSchoolYearGradeFeePayload,
  ): Promise<SchoolYearGradeFee> {
    const res = await apiClient.put(
      academicsPaths.schoolYearGradeFee(schoolYearId, gradeLevelId),
      payload,
    )
    const raw = res.data.data ?? res.data
    return normalizeSchoolYearGradeFee(raw as Record<string, unknown>)
  },
}

export const schoolYearOverviewApi = {
  async get(schoolYearId: string): Promise<SchoolYearOverview> {
    const res = await apiClient.get(academicsPaths.schoolYearOverview(schoolYearId))
    return res.data.data ?? res.data
  },
}

export const gradebookApi = {
  async listAssignments(
    academicClassId: string,
    markingPeriodId: string,
  ): Promise<GradebookAssignment[]> {
    const res = await apiClient.get(
      academicsPaths.classMarkingPeriodAssignments(academicClassId, markingPeriodId),
    )
    return res.data.data ?? res.data
  },

  async createAssignment(
    academicClassId: string,
    markingPeriodId: string,
    payload: CreateGradebookAssignmentPayload,
  ): Promise<GradebookAssignment> {
    const res = await apiClient.post(
      academicsPaths.classMarkingPeriodAssignments(academicClassId, markingPeriodId),
      payload,
    )
    return res.data.data ?? res.data
  },

  async getAssignment(assignmentId: string): Promise<GradebookAssignment> {
    const res = await apiClient.get(academicsPaths.assignment(assignmentId))
    return res.data.data ?? res.data
  },

  async updateAssignment(
    assignmentId: string,
    payload: UpdateGradebookAssignmentPayload,
  ): Promise<GradebookAssignment> {
    const res = await apiClient.patch(academicsPaths.assignment(assignmentId), payload)
    return res.data.data ?? res.data
  },

  async deleteAssignment(assignmentId: string): Promise<void> {
    await apiClient.delete(academicsPaths.assignment(assignmentId))
  },

  async listGrades(assignmentId: string): Promise<StudentGradeEntry[]> {
    const res = await apiClient.get(academicsPaths.assignmentGrades(assignmentId))
    return res.data.data ?? res.data
  },

  async bulkSaveGrades(
    assignmentId: string,
    payload: BulkSaveGradesPayload,
  ): Promise<StudentGradeEntry[]> {
    const res = await apiClient.post(academicsPaths.assignmentGrades(assignmentId), payload)
    return res.data.data ?? res.data
  },

  async getSummary(academicClassId: string, markingPeriodId: string): Promise<GradebookSummary> {
    const res = await apiClient.get(
      academicsPaths.classMarkingPeriodGradebookSummary(academicClassId, markingPeriodId),
    )
    return res.data.data ?? res.data
  },
}

export const gradeHistoryApi = {
  async list(assignmentId: string, studentId: string): Promise<GradeChangeLog[]> {
    const res = await apiClient.get(academicsPaths.assignmentGradeHistory(assignmentId, studentId))
    return res.data.data ?? res.data
  },
}

export const gradeAuditLogApi = {
  async list(classId: string): Promise<GradeChangeLogEntry[]> {
    const res = await apiClient.get(academicsPaths.gradeAuditLog(classId))
    return res.data.data ?? res.data
  },
}

export const subjectCommentsApi = {
  async list(classId: string, markingPeriodId: string): Promise<SubjectComment[]> {
    const res = await apiClient.get(
      academicsPaths.classMarkingPeriodComments(classId, markingPeriodId),
    )
    return res.data.data ?? res.data
  },

  async upsert(
    classId: string,
    markingPeriodId: string,
    studentId: string,
    payload: UpsertSubjectCommentPayload,
  ): Promise<SubjectComment> {
    const res = await apiClient.post(
      academicsPaths.classMarkingPeriodStudentComment(classId, markingPeriodId, studentId),
      payload,
    )
    return res.data.data ?? res.data
  },

  async remove(classId: string, markingPeriodId: string, studentId: string): Promise<void> {
    await apiClient.delete(
      academicsPaths.classMarkingPeriodStudentComment(classId, markingPeriodId, studentId),
    )
  },
}

export const assignmentCategoriesApi = {
  async list(classId: string, markingPeriodId: string): Promise<AssignmentCategory[]> {
    const res = await apiClient.get(
      academicsPaths.classMarkingPeriodCategories(classId, markingPeriodId),
    )
    return res.data.data ?? res.data
  },

  async create(
    classId: string,
    markingPeriodId: string,
    payload: CreateAssignmentCategoryPayload,
  ): Promise<AssignmentCategory> {
    const res = await apiClient.post(
      academicsPaths.classMarkingPeriodCategories(classId, markingPeriodId),
      payload,
    )
    return res.data.data ?? res.data
  },

  async update(
    categoryId: string,
    payload: UpdateAssignmentCategoryPayload,
  ): Promise<AssignmentCategory> {
    const res = await apiClient.patch(academicsPaths.assignmentCategory(categoryId), payload)
    return res.data.data ?? res.data
  },

  async remove(categoryId: string): Promise<void> {
    await apiClient.delete(academicsPaths.assignmentCategory(categoryId))
  },
}

export const gradeScalesApi = {
  async list(schoolId: string): Promise<GradeScale[]> {
    const res = await apiClient.get(schoolsPaths.gradeScales(schoolId))
    return res.data.data ?? res.data
  },

  async get(gradeScaleId: string): Promise<GradeScale> {
    const res = await apiClient.get(schoolsPaths.gradeScale(gradeScaleId))
    return res.data.data ?? res.data
  },

  async create(schoolId: string, payload: CreateGradeScalePayload): Promise<GradeScale> {
    const res = await apiClient.post(schoolsPaths.gradeScales(schoolId), payload)
    return res.data.data ?? res.data
  },

  async update(gradeScaleId: string, payload: UpdateGradeScalePayload): Promise<GradeScale> {
    const res = await apiClient.patch(schoolsPaths.gradeScale(gradeScaleId), payload)
    return res.data.data ?? res.data
  },

  async remove(gradeScaleId: string): Promise<void> {
    await apiClient.delete(schoolsPaths.gradeScale(gradeScaleId))
  },

  async getRanges(gradeScaleId: string): Promise<GradeScaleRange[]> {
    const res = await apiClient.get(schoolsPaths.gradeScaleRanges(gradeScaleId))
    return res.data.data ?? res.data
  },

  async replaceRanges(
    gradeScaleId: string,
    payload: ReplaceGradeScaleRangesPayload,
  ): Promise<GradeScaleRange[]> {
    const res = await apiClient.put(schoolsPaths.gradeScaleRanges(gradeScaleId), payload)
    return res.data.data ?? res.data
  },
}

function defaultTimetableDateRange(): { date_from: string; date_to: string } {
  const end = new Date()
  end.setDate(end.getDate() + 180)
  return { date_from: localIsoDateDaysAgo(14), date_to: localIsoDate(end) }
}

export const classScheduleApi = {
  async listTimetable(
    schoolYearId: string,
    params?: TimetableListParams,
  ): Promise<ClassScheduleSlot[]> {
    const cleaned = cleanApiParams(params as Record<string, unknown> | undefined) as
      | TimetableListParams
      | undefined
    const merged: TimetableListParams = {
      ...defaultTimetableDateRange(),
      ...cleaned,
    }
    if (!merged.date_from || !merged.date_to) {
      const d = defaultTimetableDateRange()
      merged.date_from = merged.date_from ?? d.date_from
      merged.date_to = merged.date_to ?? d.date_to
    }
    const res = await apiClient.get(academicsPaths.timetable(schoolYearId), {
      params: cleanApiParams(merged as unknown as Record<string, unknown>),
    })
    const raw = res.data.data ?? res.data
    const list = Array.isArray(raw) ? raw : (raw?.results ?? raw?.items ?? [])
    return list.map((row: Record<string, unknown>) => normalizeClassScheduleSlot(row))
  },

  async listForClass(
    schoolYearId: string,
    academicClassId: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<ClassScheduleSlot[]> {
    const range =
      dateFrom && dateTo ? { date_from: dateFrom, date_to: dateTo } : defaultTimetableDateRange()
    return this.listTimetable(schoolYearId, { ...range, academic_class_id: academicClassId })
  },

  /** Dated sessions for one class between two dates (small window only - one row per date). */
  async listClassSessions(
    schoolYearId: string,
    academicClassId: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<ClassSession[]> {
    const res = await apiClient.get(academicsPaths.timetable(schoolYearId), {
      params: { academic_class_id: academicClassId, date_from: dateFrom, date_to: dateTo },
    })
    const raw = res.data.data ?? res.data
    const list: Record<string, unknown>[] = Array.isArray(raw) ? raw : []
    return list.map(r => ({
      session_date: String(r.session_date ?? ''),
      weekday: Number(r.weekday ?? 0),
      day_type_name: String(r.day_type_name ?? ''),
      period_name: String(r.period_name ?? ''),
      start_time: String(r.start_time ?? ''),
      end_time: String(r.end_time ?? ''),
      resolved_teacher_name: (r.resolved_teacher_name as string | null) ?? null,
      room_label: String(r.room_label ?? ''),
      offering_id: String(r.offering_id ?? ''),
    }))
  },

  async create(
    schoolYearId: string,
    payload: CreateScheduledOfferingPayload,
  ): Promise<ScheduledOffering> {
    const res = await apiClient.post(
      academicsPaths.schoolScheduleScheduledOfferings(schoolYearId),
      {
        day_type_period_id: payload.day_type_period_id,
        academic_class_id: payload.academic_class_id,
        teacher_id: payload.teacher_id ?? null,
        parallel_track: payload.parallel_track ?? 0,
        room_label: payload.room_label ?? '',
        is_active: payload.is_active ?? true,
      },
    )
    return (res.data.data ?? res.data) as ScheduledOffering
  },

  async bulkCreateScheduledOfferings(
    schoolYearId: string,
    payload: ScheduledClassOfferingBulkRequest,
  ): Promise<ScheduledClassOfferingBulkResponse> {
    const res = await apiClient.post(
      academicsPaths.schoolScheduleScheduledOfferingsBulk(schoolYearId),
      payload,
    )
    const raw = res.data.data ?? res.data
    return raw as ScheduledClassOfferingBulkResponse
  },

  async update(
    schoolYearId: string,
    offeringId: string,
    payload: UpdateScheduledOfferingPayload,
  ): Promise<ScheduledOffering> {
    const res = await apiClient.patch(
      academicsPaths.schoolScheduleScheduledOffering(schoolYearId, offeringId),
      payload,
    )
    return (res.data.data ?? res.data) as ScheduledOffering
  },

  async remove(schoolYearId: string, offeringId: string): Promise<void> {
    await apiClient.delete(academicsPaths.schoolScheduleScheduledOffering(schoolYearId, offeringId))
  },
}

/** Scheduled offering sub-resources + period drill-down — MAK-218 / MAK-219 */
export const scheduledOfferingsApi = {
  async listPeriodOfferings(
    schoolYearId: string,
    params: PeriodOfferingsParams,
  ): Promise<PeriodOfferingRow[]> {
    const res = await apiClient.get(academicsPaths.schoolSchedulePeriodOfferings(schoolYearId), {
      params: cleanApiParams(params as unknown as Record<string, unknown>),
    })
    const raw = res.data.data ?? res.data
    const list = Array.isArray(raw) ? raw : (raw?.results ?? raw?.items ?? raw?.offerings ?? [])
    return list as PeriodOfferingRow[]
  },

  async replaceInstructors(
    schoolYearId: string,
    offeringId: string,
    payload: OfferingInstructorsReplacePayload,
  ): Promise<void> {
    await apiClient.put(
      academicsPaths.schoolScheduleScheduledOfferingInstructors(schoolYearId, offeringId),
      payload,
    )
  },

  async listStudentOverrides(
    schoolYearId: string,
    offeringId: string,
  ): Promise<OfferingStudentOverrideRow[]> {
    const res = await apiClient.get(
      academicsPaths.schoolScheduleScheduledOfferingStudentOverrides(schoolYearId, offeringId),
    )
    const raw = res.data.data ?? res.data
    return Array.isArray(raw) ? (raw as OfferingStudentOverrideRow[]) : []
  },

  async replaceStudentOverrides(
    schoolYearId: string,
    offeringId: string,
    payload: OfferingStudentOverridesReplacePayload,
  ): Promise<void> {
    await apiClient.put(
      academicsPaths.schoolScheduleScheduledOfferingStudentOverrides(schoolYearId, offeringId),
      payload,
    )
  },

  async listConflicts(schoolYearId: string, params: ScheduleConflictsParams): Promise<unknown> {
    const res = await apiClient.get(academicsPaths.schoolScheduleConflicts(schoolYearId), {
      params: cleanApiParams(params as unknown as Record<string, unknown>),
    })
    return res.data.data ?? res.data
  },

  /** Recurring conflict patterns (one row per colliding set of classes, with names). */
  /**
   * Check proposed placements for teacher and student clashes before saving anything.
   * Results keep the order of `items`; large lists are sent in batches of 200.
   */
  async checkPlacementConflicts(
    schoolYearId: string,
    items: PlacementConflictCheckItem[],
  ): Promise<PlacementConflictCheckResult[]> {
    const out: PlacementConflictCheckResult[] = []
    for (let i = 0; i < items.length; i += 200) {
      const chunk = items.slice(i, i + 200).map((item, j) => ({ ...item, client_index: i + j }))
      const res = await apiClient.post(
        academicsPaths.schoolScheduleScheduledOfferingsCheckConflicts(schoolYearId),
        { items: chunk },
      )
      const raw = res.data.data ?? res.data
      if (Array.isArray(raw)) out.push(...(raw as PlacementConflictCheckResult[]))
    }
    return out
  },

  async listConflictGroups(
    schoolYearId: string,
    params: Omit<ScheduleConflictsParams, 'grouped'>,
  ): Promise<ScheduleConflictGroup[]> {
    const res = await apiClient.get(academicsPaths.schoolScheduleConflicts(schoolYearId), {
      params: cleanApiParams({ ...params, grouped: true } as unknown as Record<string, unknown>),
    })
    const raw = res.data.data ?? res.data
    return Array.isArray(raw) ? (raw as ScheduleConflictGroup[]) : []
  },
}

export const reportCardsApi = {
  async list(studentId: string, params?: ListReportCardsParams): Promise<ReportCardListItem[]> {
    const res = await apiClient.get(academicsPaths.studentReportCards(studentId), {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    return res.data.data ?? res.data
  },

  async get(reportCardId: string): Promise<ReportCardDetail> {
    const res = await apiClient.get(academicsPaths.reportCard(reportCardId))
    return res.data.data ?? res.data
  },

  async generate(studentId: string, payload: ReportCardGeneratePayload): Promise<ReportCardDetail> {
    const res = await apiClient.post(academicsPaths.studentReportCardsGenerate(studentId), payload)
    return res.data.data ?? res.data
  },

  async publish(reportCardId: string): Promise<ReportCardDetail> {
    const res = await apiClient.post(academicsPaths.reportCardPublish(reportCardId))
    return res.data.data ?? res.data
  },

  /** Staff: publish a corrected new version of a published report card. */
  async correct(
    reportCardId: string,
    payload: ReportCardCorrectPayload,
  ): Promise<ReportCardDetail> {
    const res = await apiClient.post(academicsPaths.reportCardCorrect(reportCardId), payload)
    return res.data.data ?? res.data
  },

  /** Staff: every version of a report card, newest first. */
  async history(reportCardId: string): Promise<ReportCardListItem[]> {
    const res = await apiClient.get(academicsPaths.reportCardHistory(reportCardId))
    return res.data.data ?? res.data
  },

  /** Queue background generation (HTTP 202); returns the (possibly already active) job. */
  async createBulkJob(
    schoolYearId: string,
    payload: ReportCardBulkGeneratePayload,
  ): Promise<ReportCardBulkJob> {
    const res = await apiClient.post(academicsPaths.reportCardBulkJobs(schoolYearId), payload)
    return res.data.data ?? res.data
  },

  async getBulkJob(schoolYearId: string, jobId: string): Promise<ReportCardBulkJob> {
    const res = await apiClient.get(academicsPaths.reportCardBulkJob(schoolYearId, jobId))
    return res.data.data ?? res.data
  },

  async status(
    schoolYearId: string,
    params: { marking_period_id: string; grade_level_id?: number },
  ): Promise<ReportCardStatusSummary> {
    const res = await apiClient.get(academicsPaths.reportCardStatus(schoolYearId), {
      params: cleanApiParams(params as unknown as Record<string, unknown>),
    })
    return res.data.data ?? res.data
  },

  async bulkPublish(
    schoolYearId: string,
    payload: { marking_period_id: string; student_ids?: string[] },
  ): Promise<ReportCardBulkPublishResult> {
    const res = await apiClient.post(academicsPaths.bulkPublishReportCards(schoolYearId), payload)
    return res.data.data ?? res.data
  },
}

export const transcriptsApi = {
  async list(studentId: string, params?: ListTranscriptsParams): Promise<TranscriptListItem[]> {
    const res = await apiClient.get(academicsPaths.studentTranscript(studentId), {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    return res.data.data ?? res.data
  },

  async get(transcriptId: string): Promise<TranscriptDetail> {
    const res = await apiClient.get(academicsPaths.transcript(transcriptId))
    return res.data.data ?? res.data
  },

  async generate(studentId: string, payload: TranscriptGeneratePayload): Promise<TranscriptDetail> {
    const res = await apiClient.post(academicsPaths.studentTranscriptGenerate(studentId), payload)
    return res.data.data ?? res.data
  },

  async publish(transcriptId: string): Promise<TranscriptDetail> {
    const res = await apiClient.post(academicsPaths.transcriptPublish(transcriptId))
    return res.data.data ?? res.data
  },
}

// ─── School-first schedule (MAK-164) ─────────────────────────────────────────

/** Backend may return `config: null` before the school year schedule is initialized. */
const DEFAULT_SCHOOL_SCHEDULE_CONFIG: SchoolScheduleConfig = {
  operating_weekdays: [0, 1, 2, 3, 4],
  school_day_start: null,
  school_day_end: null,
}

function normalizeDayTypePeriod(
  raw: Partial<DayTypePeriod> & Record<string, unknown>,
): DayTypePeriod {
  return {
    id: String(raw.id ?? ''),
    day_type_id: String(raw.day_type_id ?? raw.dayTypeId ?? ''),
    name: String(raw.name ?? ''),
    start_time: String(raw.start_time ?? raw.startTime ?? ''),
    end_time: String(raw.end_time ?? raw.endTime ?? ''),
    sequence: Number(raw.sequence ?? 0),
    is_active: Boolean(raw.is_active ?? raw.isActive ?? true),
  }
}

function normalizeDayType(raw: Partial<DayType> & Record<string, unknown>): DayType {
  const periodsRaw = raw.periods ?? raw.period
  const periods = Array.isArray(periodsRaw)
    ? periodsRaw.map(p =>
        normalizeDayTypePeriod(p as Partial<DayTypePeriod> & Record<string, unknown>),
      )
    : undefined
  return {
    id: String(raw.id ?? ''),
    school_year_id: String(raw.school_year_id ?? raw.schoolYearId ?? ''),
    name: String(raw.name ?? ''),
    sort_order:
      raw.sort_order != null || raw.sortOrder != null
        ? Number(raw.sort_order ?? raw.sortOrder)
        : undefined,
    is_default:
      raw.is_default != null || raw.isDefault != null
        ? Boolean(raw.is_default ?? raw.isDefault)
        : undefined,
    periods,
  }
}

function pickNumArray(v: unknown): number[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out = v.map(x => Number(x)).filter(n => !Number.isNaN(n))
  return out.length ? out : undefined
}

function pickStrArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined
  return v.map(x => String(x))
}

/** Merge defaults + API `config` and accept camelCase keys from the backend. */
function normalizeSchoolScheduleConfig(
  raw: (Partial<SchoolScheduleConfig> & Record<string, unknown>) | null | undefined,
): SchoolScheduleConfig {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_SCHOOL_SCHEDULE_CONFIG }
  }
  const r = raw as Record<string, unknown>
  const operating_weekdays =
    pickNumArray(r.operating_weekdays) ??
    pickNumArray(r.operatingWeekdays) ??
    DEFAULT_SCHOOL_SCHEDULE_CONFIG.operating_weekdays

  // The API names these `school_day_start_time` / `school_day_end_time`.
  const startRaw = r.school_day_start_time ?? r.school_day_start ?? r.schoolDayStart
  const endRaw = r.school_day_end_time ?? r.school_day_end ?? r.schoolDayEnd
  const school_day_start = typeof startRaw === 'string' && startRaw.trim() ? startRaw.trim() : null
  const school_day_end = typeof endRaw === 'string' && endRaw.trim() ? endRaw.trim() : null
  const anchorRaw = r.rotation_anchor_date
  const cycleRaw = r.rotation_cycle_weeks

  return {
    operating_weekdays,
    school_day_start,
    school_day_end,
    ...(typeof anchorRaw === 'string' && anchorRaw ? { rotation_anchor_date: anchorRaw } : {}),
    ...(typeof cycleRaw === 'number' ? { rotation_cycle_weeks: cycleRaw } : {}),
  }
}

export function normalizeClassScheduleSlot(raw: Record<string, unknown>): ClassScheduleSlot {
  const r = raw as Record<string, unknown>
  const teacher_ids = pickNumArray(r.teacher_ids) ?? pickNumArray(r.teacherIds)
  const parallelRaw = r.parallel_track ?? r.parallelTrack
  return {
    id: String(r.id ?? ''),
    school_year_id: String(r.school_year_id ?? r.schoolYearId ?? ''),
    academic_class_id: String(r.academic_class_id ?? r.academicClassId ?? ''),
    academic_class_name: String(r.academic_class_name ?? r.academicClassName ?? ''),
    ...(parallelRaw != null && parallelRaw !== '' ? { parallel_track: Number(parallelRaw) } : {}),
    weekday: Number(r.weekday ?? 0),
    start_time: String(r.start_time ?? r.startTime ?? ''),
    end_time: String(r.end_time ?? r.endTime ?? ''),
    effective_start_date: String(r.effective_start_date ?? r.effectiveStartDate ?? ''),
    effective_end_date: String(r.effective_end_date ?? r.effectiveEndDate ?? ''),
    teacher_override_id: (r.teacher_override_id ?? r.teacherOverrideId ?? null) as number | null,
    resolved_teacher_id: (r.resolved_teacher_id ?? r.resolvedTeacherId ?? null) as number | null,
    resolved_teacher_name: (r.resolved_teacher_name ?? null) as string | null,
    teacher_ids,
    room_label: String(r.room_label ?? r.roomLabel ?? ''),
    slot_key: String(r.slot_key ?? r.slotKey ?? ''),
    is_active: Boolean(r.is_active ?? r.isActive ?? true),
    created_at: String(r.created_at ?? r.createdAt ?? ''),
    updated_at: String(r.updated_at ?? r.updatedAt ?? ''),
    day_type_period_id: (r.day_type_period_id ?? r.dayTypePeriodId ?? null) as
      | string
      | null
      | undefined,
    day_type_id: (r.day_type_id ?? r.dayTypeId ?? null) as string | null | undefined,
    day_type_name: (r.day_type_name ?? r.dayTypeName ?? null) as string | null | undefined,
    day_type_period_name: (r.day_type_period_name ?? r.dayTypePeriodName ?? null) as
      | string
      | null
      | undefined,
    target_grade_level_ids: pickNumArray(r.target_grade_level_ids ?? r.targetGradeLevelIds),
    target_grade_section_ids: pickNumArray(r.target_grade_section_ids ?? r.targetGradeSectionIds),
    target_student_ids: pickStrArray(r.target_student_ids ?? r.targetStudentIds),
  }
}

function parseRotationEntries(raw: unknown): RotationWeekdayAssignmentInput[] {
  if (!Array.isArray(raw)) return []
  const out: RotationWeekdayAssignmentInput[] = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const o = row as Record<string, unknown>
    const cwi = Number(o.cycle_week_index ?? o.cycleWeekIndex ?? 0)
    const wd = Number(o.weekday)
    const dt = String(o.day_type_id ?? o.dayTypeId ?? '').trim()
    if (!Number.isFinite(wd) || !dt) continue
    out.push({
      cycle_week_index: Number.isFinite(cwi) ? cwi : 0,
      weekday: wd,
      day_type_id: dt,
    })
  }
  return out
}

function weekdayMapFromRotationEntries(
  entries: unknown,
): Record<string, string | null> | undefined {
  if (!Array.isArray(entries)) return undefined
  const map: Record<string, string | null> = {}
  for (const row of entries) {
    if (!row || typeof row !== 'object') continue
    const o = row as Record<string, unknown>
    const cwi = Number(o.cycle_week_index ?? o.cycleWeekIndex ?? 0)
    if (cwi !== 0) continue
    const wd = Number(o.weekday)
    const dt = o.day_type_id ?? o.dayTypeId
    if (!Number.isFinite(wd)) continue
    if (typeof dt === 'string' && dt) map[String(wd)] = dt
  }
  return Object.keys(map).length ? map : undefined
}

function normalizeSchoolSchedule(
  raw: SchoolSchedule & { config: SchoolScheduleConfig | null },
): SchoolSchedule {
  const ext = raw as unknown as Record<string, unknown>
  const mergedConfig = normalizeSchoolScheduleConfig({
    ...DEFAULT_SCHOOL_SCHEDULE_CONFIG,
    ...(raw.config ?? {}),
  } as Partial<SchoolScheduleConfig> & Record<string, unknown>)
  const dayTypesRaw = ext.day_types ?? ext.dayTypes
  const day_types =
    dayTypesRaw !== undefined && Array.isArray(dayTypesRaw)
      ? dayTypesRaw.map(d => normalizeDayType(d as Partial<DayType> & Record<string, unknown>))
      : undefined
  let weekday_day_type_map =
    (ext.weekday_day_type_map as Record<string, string | null> | undefined) ??
    (ext.weekdayDayTypeMap as Record<string, string | null> | undefined)
  const rotEntries = extractRotationWeekdayRowsFromSchedulePayload(ext)
  if (weekday_day_type_map === undefined) {
    const derived = weekdayMapFromRotationEntries(rotEntries)
    if (derived) weekday_day_type_map = derived
  }

  const rotation_entries = parseRotationEntries(rotEntries)

  const offeringsRaw = ext.scheduled_offerings
  return {
    ...raw,
    scheduled_offerings: Array.isArray(offeringsRaw) ? (offeringsRaw as ScheduledOffering[]) : [],
    config: mergedConfig,
    ...(day_types !== undefined ? { day_types } : {}),
    ...(weekday_day_type_map !== undefined ? { weekday_day_type_map } : {}),
    ...(rotation_entries.length ? { rotation_entries } : {}),
  }
}

/** Map the UI payload to the API's field names (`*_time`, explicit unset flags). */
export function toScheduleConfigWire(
  payload: PatchSchoolScheduleConfigPayload,
): Record<string, unknown> {
  const { school_day_start, school_day_end, rotation_anchor_date, ...rest } = payload
  const wire: Record<string, unknown> = { ...rest }
  if (school_day_start !== undefined) {
    if (school_day_start) wire.school_day_start_time = school_day_start
    else wire.unset_school_day_start = true
  }
  if (school_day_end !== undefined) {
    if (school_day_end) wire.school_day_end_time = school_day_end
    else wire.unset_school_day_end = true
  }
  if (rotation_anchor_date !== undefined) {
    if (rotation_anchor_date) wire.rotation_anchor_date = rotation_anchor_date
    else wire.unset_rotation_anchor = true
  }
  return wire
}

export const schoolScheduleApi = {
  async get(schoolYearId: string): Promise<SchoolSchedule> {
    const res = await apiClient.get(academicsPaths.schoolSchedule(schoolYearId))
    const data = res.data.data ?? res.data
    return normalizeSchoolSchedule(data as SchoolSchedule & { config: SchoolScheduleConfig | null })
  },

  async patchConfig(
    schoolYearId: string,
    payload: PatchSchoolScheduleConfigPayload,
  ): Promise<SchoolSchedule> {
    const res = await apiClient.patch(
      academicsPaths.schoolScheduleConfig(schoolYearId),
      toScheduleConfigWire(payload),
    )
    const data = res.data.data ?? res.data
    return normalizeSchoolSchedule(data as SchoolSchedule & { config: SchoolScheduleConfig | null })
  },

  async preview(
    schoolYearId: string,
    params: SchoolSchedulePreviewParams,
  ): Promise<SchedulePreviewResult> {
    const res = await apiClient.get(academicsPaths.schoolSchedulePreview(schoolYearId), {
      params: cleanApiParams(params as unknown as Record<string, unknown>),
    })
    return res.data.data ?? res.data
  },

  async resolvedSessions(
    schoolYearId: string,
    params: ResolvedSessionsParams,
  ): Promise<ResolvedSessionsResult> {
    const res = await apiClient.get(academicsPaths.schoolScheduleResolvedSessions(schoolYearId), {
      params: cleanApiParams(params as unknown as Record<string, unknown>),
    })
    return res.data.data ?? res.data
  },
}

/** Day types + rotation — MAK-218 / MAK-222 */
export const dayTypesScheduleApi = {
  async listDayTypes(schoolYearId: string): Promise<DayType[]> {
    const schedule = await schoolScheduleApi.get(schoolYearId)
    return schedule.day_types ?? []
  },

  async putRotationWeekdayAssignments(
    schoolYearId: string,
    payload: PutRotationWeekdayAssignmentsPayload,
  ): Promise<void> {
    await apiClient.put(academicsPaths.schoolScheduleRotationWeekdayAssignments(schoolYearId), {
      entries: payload.entries,
    })
  },

  async createDayType(schoolYearId: string, payload: CreateDayTypePayload): Promise<DayType> {
    const res = await apiClient.post(academicsPaths.schoolScheduleDayTypes(schoolYearId), payload)
    return res.data.data ?? res.data
  },

  async updateDayType(
    schoolYearId: string,
    dayTypeId: string,
    payload: UpdateDayTypePayload,
  ): Promise<DayType> {
    const res = await apiClient.patch(
      academicsPaths.schoolScheduleDayType(schoolYearId, dayTypeId),
      payload,
    )
    return res.data.data ?? res.data
  },

  async deleteDayType(schoolYearId: string, dayTypeId: string): Promise<void> {
    await apiClient.delete(academicsPaths.schoolScheduleDayType(schoolYearId, dayTypeId))
  },

  async createPeriod(
    schoolYearId: string,
    dayTypeId: string,
    payload: CreateDayTypePeriodPayload,
  ): Promise<DayTypePeriod> {
    const res = await apiClient.post(
      academicsPaths.schoolScheduleDayTypePeriods(schoolYearId, dayTypeId),
      payload,
    )
    return res.data.data ?? res.data
  },

  /** Atomically replace every period of a day type (`dry_run` only reports what would go). */
  async replacePeriods(
    schoolYearId: string,
    dayTypeId: string,
    payload: ReplaceDayTypePeriodsPayload,
  ): Promise<ReplaceDayTypePeriodsResult> {
    const res = await apiClient.put(
      academicsPaths.schoolScheduleDayTypePeriods(schoolYearId, dayTypeId),
      payload,
    )
    return res.data.data ?? res.data
  },

  async updatePeriod(
    schoolYearId: string,
    periodId: string,
    payload: UpdateDayTypePeriodPayload,
  ): Promise<DayTypePeriod> {
    const res = await apiClient.patch(
      academicsPaths.schoolScheduleDayTypePeriod(schoolYearId, periodId),
      payload,
    )
    return res.data.data ?? res.data
  },

  async deletePeriod(schoolYearId: string, periodId: string): Promise<void> {
    await apiClient.delete(academicsPaths.schoolScheduleDayTypePeriod(schoolYearId, periodId))
  },
}

function numFromApi(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Maps `/me/assignments/upcoming/` rows — backend may use alternate keys for grading_progress. */
function normalizeTeacherUpcomingAssignment(raw: unknown): TeacherUpcomingAssignment {
  if (!raw || typeof raw !== 'object') {
    return {
      assignment_id: '',
      title: '',
      class_id: '',
      class_name: '',
      due_date: '',
      grading_progress: { graded: 0, total: 0 },
    }
  }
  const o = raw as Record<string, unknown>
  const gp = o.grading_progress ?? o.gradingProgress
  let graded = 0
  let total = 0
  if (gp && typeof gp === 'object') {
    const g = gp as Record<string, unknown>
    graded = numFromApi(
      g.graded ??
        g.graded_count ??
        g.entries_graded ??
        g.submissions_graded ??
        g.graded_submissions,
    )
    total = numFromApi(
      g.total ??
        g.total_count ??
        g.roster_count ??
        g.expected ??
        g.expected_grades ??
        g.student_count,
    )
  }
  const ungraded = numFromApi(
    o.ungraded_count ?? o.ungraded_submissions ?? o.pending_grade_count ?? o.pending_grades,
  )
  if (ungraded > 0) {
    if (total > 0) {
      graded = Math.min(total, Math.max(0, total - ungraded))
    } else {
      total = ungraded
      graded = 0
    }
  }
  return {
    assignment_id: String(o.assignment_id ?? o.id ?? ''),
    title: String(o.title ?? ''),
    class_id: String(o.class_id ?? o.academic_class_id ?? ''),
    class_name: String(o.class_name ?? o.academic_class_name ?? ''),
    due_date: String(o.due_date ?? ''),
    grading_progress: { graded, total },
  }
}

/** Teacher dashboard — `/academics/me/*` (MAK-196) */
export const teacherMeApi = {
  async listClasses(params?: TeacherMeQueryParams): Promise<AcademicClass[]> {
    const res = await apiClient.get(academicsPaths.teacherMeClasses, {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    const items = res.data.data ?? res.data
    return Array.isArray(items) ? items.map(normalizeAcademicClass) : []
  },

  async timetable(params: TeacherMeQueryParams): Promise<TeacherTimetableSession[]> {
    const res = await apiClient.get(academicsPaths.teacherMeTimetable, {
      params: cleanApiParams(params as Record<string, unknown>),
    })
    const raw = res.data.data ?? res.data
    const list: Record<string, unknown>[] = Array.isArray(raw) ? raw : []
    return list.map(r => ({
      session_date: String(r.session_date ?? ''),
      weekday: Number(r.weekday ?? 0),
      period_name: String(r.period_name ?? ''),
      start_time: String(r.start_time ?? ''),
      end_time: String(r.end_time ?? ''),
      academic_class_id: String(r.academic_class_id ?? ''),
      academic_class_name: String(r.academic_class_name ?? ''),
      room_label: String(r.room_label ?? ''),
      offering_id: String(r.offering_id ?? ''),
    }))
  },

  async attentionList(params?: TeacherMeQueryParams): Promise<TeacherAttentionListItem[]> {
    const res = await apiClient.get(academicsPaths.teacherMeAttentionList, {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    return res.data.data ?? res.data
  },

  async upcomingAssignments(params?: TeacherMeQueryParams): Promise<TeacherUpcomingAssignment[]> {
    const res = await apiClient.get(academicsPaths.teacherMeAssignmentsUpcoming, {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    const items = res.data.data ?? res.data
    return Array.isArray(items) ? items.map(normalizeTeacherUpcomingAssignment) : []
  },

  async weeklyAttendance(params?: TeacherMeQueryParams): Promise<{
    rows: TeacherWeeklyClassAttendance[]
    todayMeanAttendanceRate: number | null
  }> {
    const res = await apiClient.get(academicsPaths.teacherMeAttendanceWeekly, {
      params: cleanApiParams(params as Record<string, unknown> | undefined),
    })
    const raw: TeacherWeeklyClassAttendance[] | TeacherWeeklyAttendancePayload | unknown =
      res.data.data ?? res.data
    if (Array.isArray(raw)) {
      return { rows: raw, todayMeanAttendanceRate: null }
    }
    if (raw && typeof raw === 'object' && 'classes' in raw) {
      const p = raw as TeacherWeeklyAttendancePayload
      return {
        rows: p.classes ?? [],
        todayMeanAttendanceRate: p.today_mean_attendance_rate ?? null,
      }
    }
    return { rows: [], todayMeanAttendanceRate: null }
  },
}
